// Game Configuration Constants
import type { GameConfig } from './types';

export const CONFIG: GameConfig = {
    // Canvas settings (Portrait: 9:16, Landscape: 16:9)
    CANVAS_WIDTH: 720,
    CANVAS_HEIGHT: 1280,
    CANVAS_WIDTH_LANDSCAPE: 1280,
    CANVAS_HEIGHT_LANDSCAPE: 720,
    BACKGROUND_COLOR: '#000000',
    TARGET_FPS: 60,

    // Background transition (5초마다 화이트↔블랙 전환)
    BACKGROUND: {
        TRANSITION_INTERVAL: 5.0,  // seconds (10초에서 절반으로 줄임)
        FADE_DURATION: 1.0          // seconds for fade transition (2초에서 절반으로 줄임)
    },

    // Player settings
    PLAYER: {
        RADIUS: 12,   // 2배 (6 → 12)
        SPEED: 190,   // PixiJS 도입 후 fps 안정화로 속도 조정 (250 → 190)
        COLOR: '#FFFFFF',
        START_X: 360, // Center of canvas (2배)
        START_Y: 640  // Center of canvas (1280 / 2)
    },

    // Bullet settings
    BULLET: {
        RADIUS: 2,        // 2배 (1 → 2)
        SPEED_MIN: 55,    // 속도 감소 (70 → 55)
        SPEED_MAX: 110,   // 속도 감소 (130 → 110)
        // 색상은 랜덤 생성으로 변경 (COLORS 배열 제거)
    },

    // One-way bullet settings (단방향 총알)
    ONE_WAY_BULLET: {
        WIDTH: 36,                        // 고정 가로 (절반)
        HEIGHT_MIN: 25,                   // 최소 세로 (절반)
        HEIGHT_MAX: 50,                   // 최대 세로 (절반)
        SPEED: 350,                       // 속도 감소 (450 → 350)
        SPAWN_INTERVAL: 0.3               // 스폰 간격 증가 (0.2 → 0.3)
    },

    // Difficulty progression
    DIFFICULTY: {
        SPAWN_INTERVAL_START: 0.8,   // 더 느린 시작 스폰 (0.668 → 0.8)
        SPAWN_INTERVAL_MIN: 0.18,    // 최소 스폰 간격 증가 (0.132 → 0.18)
        RAMP_TIME: 120,              // 난이도 상승 시간 증가 (90 → 120초)

        // 처음부터 다양한 각도 - 패턴을 모두 0초부터 시작
        PATTERN_SIMPLE_MAX: 0,       // 즉시 건너뜀
        PATTERN_MEDIUM_MAX: 30,      // 0-30s: medium (angled) - 10초 연장
        PATTERN_HARD_MAX: 55,        // 30-55s: hard (multi) - 15초 연장
        PATTERN_EXTREME_MAX: 80      // 55-80s: extreme (circular), 80+: sustained
    },

    // Play area border
    PLAY_AREA: {
        MARGIN: 20,      // 2배 (10 → 20)
        BORDER_COLOR: '#FFFFFF',
        BORDER_WIDTH: 1  // Thin border for white on gradient
    },

    // Input settings
    INPUT: {
        KEYBOARD: {
            UP: ['ArrowUp', 'w', 'W'],
            DOWN: ['ArrowDown', 's', 'S'],
            LEFT: ['ArrowLeft', 'a', 'A'],
            RIGHT: ['ArrowRight', 'd', 'D'],
            CONFIRM: ['z', 'Z', 'Enter', ' '],
            CANCEL: ['x', 'X', 'Escape']
        },
        GAMEPAD: {
            DEADZONE: 0.2,
            BUTTON_CONFIRM: 0,  // A button
            BUTTON_CANCEL: 1    // B button
        },
        TOUCH: {
            JOYSTICK_SIZE: 200,  // 2배 (100 → 200)
            JOYSTICK_DEADZONE: 0.15,
            BUTTON_SIZE: 120     // 2배 (60 → 120)
        }
    },

    // UI settings
    UI: {
        FONT_FAMILY: 'Pretendard, Arial, sans-serif',
        TITLE_SIZE: 90,  // Reduced from 128 to fit "Spectrum Survive"
        GAMEOVER_SIZE: 85, // TITLE_SIZE의 2/3 (128 * 2/3 ≈ 85)
        SUBTITLE_SIZE: 48, // 2배 (24 → 48)
        SCORE_SIZE: 40     // 2배 (20 → 40)
    },

    // Score settings
    SCORE: {
        STORAGE_KEY: 'daynight_highscore'
    },

    // Options settings
    OPTIONS: {
        DIFFICULTY: 'normal',  // 'easy', 'normal', 'hard'
        SFX_ENABLED: true,
        BGM_ENABLED: true,
        BGM_TRACK: 'Dream Veil',  // 기본 BGM
        BG_STYLE: 'stripe',    // 'stripe', 'diamond', 'triangle'
        BIGBALL_MODE: false,   // 빅볼 모드: 총알 8배 크기, 속도 2/3, 점수 2배
        STORAGE_KEY: 'daynight_options'
    },

    // BigBall mode settings
    BIGBALL: {
        BULLET_SIZE_MULT: 8.0,  // 총알 크기 8배
        SPEED_MULT: 2/3,        // 이동속도 2/3
        SCORE_MULT: 2.0         // 점수 2배
    },

    // Stats settings
    STATS: {
        STORAGE_KEY: 'daynight_stats'
    },

    // Achievements settings
    ACHIEVEMENTS: {
        STORAGE_KEY: 'daynight_achievements'
    },

    // Difficulty multipliers
    DIFFICULTY_SETTINGS: {
        easy: {
            SPAWN_INTERVAL_MULT: 2.0,    // 2배 느린 스폰
            BULLET_SPEED_MULT: 0.5,      // 절반 속도
            RAMP_TIME_MULT: 2.0,         // 2배 느린 난이도 상승
            SCORE_MULT: 1/3              // 점수 1/3
        },
        normal: {
            SPAWN_INTERVAL_MULT: 1.0,
            BULLET_SPEED_MULT: 1.0,
            RAMP_TIME_MULT: 1.0,
            SCORE_MULT: 1.0
        },
        hard: {
            SPAWN_INTERVAL_MULT: 0.5,    // 2배 빠른 스폰
            BULLET_SPEED_MULT: 1.5,      // 1.5배 빠른 총알
            RAMP_TIME_MULT: 0.5,         // 2배 빠른 난이도 상승
            SCORE_MULT: 2.0              // 점수 2배
        }
    },

    // Game screen settings
    GAME_SCREEN: {
        GAME_OVER_DELAY: 1.5,           // 게임오버 후 UI 표시까지 대기 시간
        BULLET_STOP_TIME: 30.0,         // 게임오버 후 총알 생성 중지 시간
        HEARTS_TO_EVOLVE: 2,            // 진화에 필요한 하트 개수
        HIT_INVINCIBILITY_DURATION: 1.0, // 피격 후 무적 시간
        EXPLOSION_PARTICLE_COUNT: 20,    // 폭발 파티클 개수
        SPEED_LINE_SIZE: 60,            // 스피드 라인 크기
        SPEED_LINE_SPACING: 120,        // 스피드 라인 간격
        SPEED_LINE_SPEED: 450,          // 스피드 라인 속도
        BUTTON_WIDTH: 300,              // 버튼 가로 크기
        BUTTON_HEIGHT: 80,              // 버튼 세로 크기
        BUTTON_SPACING: 20,             // 버튼 간격
        PLAY_BUTTON_RADIUS: 60          // 일시정지 재생 버튼 반지름
    },

    // Power-up settings
    POWERUP: {
        LIGHTNING: {
            SPEED_MULT: 1.5,        // 플레이어 속도 1.5배
            DURATION: 10.0          // 10초 지속
        },
        CLOCK: {
            BULLET_SPEED_MULT: 0.5, // 총알 속도 0.5배
            DURATION: 5.0           // 5초 지속
        },
        MAGNET: {
            RANGE: 200,             // 흡인 범위 (증가)
            DURATION: 20.0,         // 20초 지속
            ATTRACTION_SPEED: 400   // 흡인 속도 (증가)
        },
        BOMB: {
            // 즉시 효과, 지속시간 없음
        }
    },

    // Combo settings
    COMBO: {
        NEAR_MISS_DISTANCE: 50,     // Near Miss 판정 거리
        NEAR_MISS_POINTS: 10,       // Near Miss 기본 점수
        COMBO_TIMEOUT: 2.0,         // 콤보 유지 시간
        PERFECT_THRESHOLD: 10,      // PERFECT 달성 콤보 수
        MULTIPLIERS: {
            5: 1.5,                 // 5콤보: 1.5배
            10: 2.0                 // 10콤보: 2배
        }
    },

    // Slow motion settings
    SLOWMO: {
        TIME_SCALE: 0.3,            // 30% 속도
        DURATION: 0.5,              // 0.5초 지속
        FADE_DURATION: 0.1          // 페이드 인/아웃 시간
    },

    // Screen shake settings
    SCREEN_SHAKE: {
        INTENSITY: 8,               // 기본 흔들림 강도
        DURATION: 0.2,              // 흔들림 지속 시간
        DECAY: 0.9                  // 감쇠율
    },

    // Boss settings
    BOSS: {
        SPAWN_INTERVAL: 60,         // 60초 후 첫 보스, 이후 보스 죽고 60초 후 다음 보스
        WARNING_DURATION: 2.0,      // 보스 경고 표시 시간
        DEFEAT_TIME: 15,            // 15초 후 보스 자동 처치
        ROTATING_TURRET: {
            HEALTH: 100,
            ROTATION_SPEED: Math.PI / 2,  // 초당 90도
            FIRE_INTERVAL: 0.5,
            BULLET_COUNT: 8
        },
        CHASER: {
            HEALTH: 100,
            MOVE_SPEED: 80,
            FIRE_INTERVAL: 1.0,
            BULLET_COUNT: 6
        },
        SPLITTER: {
            HEALTH: 100,
            MOVE_SPEED: 60,
            FIRE_INTERVAL: 1.2,
            BASE_RADIUS: 45,
            MAX_SPLIT_LEVEL: 2      // 최대 분열 단계 (0, 1, 2)
        },
        MIN_DISTANCE: 80            // Chaser 최소 거리
    },

    // Laser settings
    LASER: {
        WARNING_DURATION: 1.5,      // 경고 표시 시간 (1.5초)
        ACTIVE_DURATION: 0.3,       // 레이저 발사 시간
        WIDTH: 20,                  // 레이저 폭
        SPAWN_START_TIME: 50        // 50초 이후부터 스폰 (40 → 50)
    },

    // Danger warning settings
    DANGER_WARNING: {
        DISTANCE: 100,              // 경고 표시 거리
        ARROW_SIZE: 20              // 화살표 크기
    },

    // Item spawn probabilities (must sum to 1.0)
    ITEM_SPAWN: {
        HEART: 0.50,                // 50%
        STAR: 0.20,                 // 20%
        LIGHTNING: 0.10,            // 10%
        CLOCK: 0.08,                // 8%
        BOMB: 0.05,                 // 5%
        MAGNET: 0.07                // 7%
    }
};
